import { PRICING_MODELS } from '@vy/types';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { VyDescriptionTitle } from '../../../../../components/new-design/data-display/VyDescriptionTitle/VyDescriptionTitle';
import { VyRadio } from '../../../../../components/new-design/inputs/VyRadio/VyRadio';
import { VyRadioGroup } from '../../../../../components/new-design/inputs/VyRadio/VyRadioGroup';
import {
  PRO_RATA_SETTINGS,
  type ProRataSettings,
  mapProRataSettingToProductFields,
} from '../CreatePlanForm/create-plan-form-types/create-plan-form-sub-types';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { prorationOptions } from '../ProRataRadioGroup/proration-options';

type PlanBillingMethodRadioGroupProps = {
  disabled?: boolean;
};

export const PlanBillingMethodRadioGroup: React.FC<PlanBillingMethodRadioGroupProps> = ({
  disabled = false,
}) => {
  const { watch, setValue, getValues } = useFormContext<PlanInputForm>();

  const proRataSetting = watch('proRataSetting');
  const currentValue = proRataSetting ?? PRO_RATA_SETTINGS.PRO_RATA_AND_CALENDAR_ALIGNED;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSetting = event.target.value as ProRataSettings;
    const { proRata, isCalendarAligned } = mapProRataSettingToProductFields(newSetting);

    setValue('proRataSetting', newSetting, { shouldDirty: true });

    const fixedProducts = getValues('fixedProducts') ?? [];
    const usageProducts = getValues('usageProducts') ?? [];
    const promotionProducts = getValues('promotionProducts') ?? [];

    fixedProducts.forEach((product, i) => {
      if (product.variants[0].pricing.pricingModel === PRICING_MODELS.FixedOneTime) {
        return;
      }

      const effectiveCalendarAligned = product.isCalendarAlignedOverridden
        ? product.isCalendarAligned
        : isCalendarAligned;

      if (!product.isCalendarAlignedOverridden) {
        setValue(`fixedProducts.${i}.isCalendarAligned`, isCalendarAligned, { shouldDirty: true });
      }

      setValue(`fixedProducts.${i}.proRata`, proRata || effectiveCalendarAligned, { shouldDirty: true });
    });

    usageProducts.forEach((product, i) => {
      const effectiveCalendarAligned = product.isCalendarAlignedOverridden
        ? product.isCalendarAligned
        : isCalendarAligned;

      if (!product.isCalendarAlignedOverridden) {
        setValue(`usageProducts.${i}.isCalendarAligned`, isCalendarAligned, { shouldDirty: true });
      }

      setValue(`usageProducts.${i}.proRata`, proRata || effectiveCalendarAligned, { shouldDirty: true });
    });

    promotionProducts.forEach((product, i) => {
      const effectiveCalendarAligned = product.isCalendarAlignedOverridden
        ? product.isCalendarAligned
        : isCalendarAligned;

      if (!product.isCalendarAlignedOverridden) {
        setValue(`promotionProducts.${i}.isCalendarAligned`, isCalendarAligned, { shouldDirty: true });
      }

      setValue(`promotionProducts.${i}.proRata`, proRata || effectiveCalendarAligned, { shouldDirty: true });
    });
  };

  const options = useMemo(
    () =>
      prorationOptions.map(({ label, value }) => ({
        value,
        label: <VyDescriptionTitle title={label.title} subTitle={label.subTitle} />,
      })),
    [],
  );

  return (
    <VyRadioGroup
      value={currentValue}
      onChange={handleChange}
      horizontal
      options={options}
      renderOption={(item, index, isActive) => (
        <VyRadio
          key={index}
          value={item.value}
          label={item.label}
          disabled={disabled}
          labelSx={{
            padding: '24px',
            flex: 1,
            border: `1px solid ${isActive ? '#5083FF' : '#E8EBF2'}`,
            borderRadius: '8px',
            backgroundColor: `${isActive ? '#F5F6FA' : '#FFFFFF'}`,
            '&:hover': {
              backgroundColor: '#F5F6FA',
              borderColor: '#B0D2FF',
            },
          }}
        />
      )}
    />
  );
};

